import { getAllIpa, IpaType, ipaCombiners } from "./ipa";
import { PhonemeItem, segmentPhonemes } from "./segmentPhonemes";

/** figure out which phonemes could be combined, defaults to all IPA if 'possible' isn't passed in */
export function getPossibleCombos(phonemes: PhonemeItem[], possible?: PossibleCombos): string[][] {
  if (possible === undefined) {
    if (ipaPossible === null) {
      ipaPossible = new PossibleCombos(getAllIpa().ipa);
    }
    possible = ipaPossible;
  }

  return possible.getPossibleCombos(phonemes);
}
let ipaPossible: PossibleCombos | null = null;

/** if 'ipa' is an affricate or co-articulated consonant without a tiebar, return it with a tiebar, else null */
export function getWithTie(ipa: IpaType, possible?: PossibleCombos): IpaType | null {
  const segments = segmentPhonemes(ipa);
  if (segments.phonemes.length < 2) {
    return null;
  }
  const combos = getPossibleCombos(segments.phonemes, possible);
  return combos.length === 0 ? null : combos[0][1];
}

/** cache info about possible symbols to combine */
export class PossibleCombos {
  private readonly possible: string[] = [];
  private readonly map: Record<IpaType, IpaType> = {};

  constructor(all: IpaType[]) {
    for (const s of all) {
      if (s.length >= 3 && s.includes('\u0361')) {
        const without = s.replace(/\u0361/g, '');
        this.possible.push(without);
        this.map[without] = s;
      }
    }
  }

  /** find everything with ties & cache possible replacements */
  getPossibleCombos(phonemes: PhonemeItem[]): string[][] {
    const results: string[][] = [];
    if (phonemes.length === 0) {
      return results;
    }

    this.checkCombining(phonemes, results);
    this.checkClicks(phonemes, results);
    return results;
  }

  /** check if inserting a tie-bar matches known IPA */
  checkCombining(phonemes: PhonemeItem[], results: string[][]) {
    let prev = phonemes[0].phoneme;
    for (let i = 1; i < phonemes.length; i++) {
      // combine last & current phonemes to see if they could be replaced
      const curr = phonemes[i].phoneme;
      const both = prev + curr;
      const bothTrunc = prev[0] + curr[0];
      // run thru all possible replacements to see if there's a match
      for (const p of this.possible) {
        if (bothTrunc === p) { // ignore trailing diacritics
          if (!results.some(r => r[0] === both)) { // only add if not already added
            const into = prev + ipaCombiners[0] + curr;
            results.push([both, into]);
          }
        }
      }

      prev = curr;
    }
  }

  /** check various click combos */
  checkClicks(phonemes: PhonemeItem[], results: string[][]) {
    // manner of articulation
    const click1Map: Record<string, string> = {
      k: 'k', ɡ: 'ɡ', ŋ: 'ŋ', 'q': 'q', ɴ: 'ɴ', 'g': 'ɡ',
      ᵏ: 'k', ᶢ: 'ɡ', ᵑ: 'ŋ', '𐞥': 'q', ᶰ: 'ɴ',
    };
    // alternate click symbols
    const click2Map: Record<string, string> = {
      ʘ: 'ʘ', ǀ: 'ǀ', ǃ: 'ǃ', '‼': '‼', 'ǂ': 'ǂ', ǁ: 'ǁ',
      ɋ: 'ʘ', ʇ: 'ǀ', ʗ: 'ǃ', ψ: '‼', '⨎': 'ǂ', ʖ: 'ǁ',
    };
    // non-general combos
    const special: Record<string, string> = {
      ᵇʘ: 'ɡ͡ʘ', ᵈǀ: 'ɡ͡ǀ', ᵐʘ: 'ŋ͡ʘ', ⁿǀ: 'ŋ͡ǀ',
    }

    let prevP = phonemes[0].phoneme;
    let prevC = click1Map[prevP];
    for (let i = 1; i < phonemes.length; i++) {
      const currP = phonemes[i].phoneme;
      const currC = click2Map[currP];

      const toSpecial = special[prevP + currP];
      if (toSpecial !== undefined) {
        // a few special combos
        const from = prevP + currP;
        results.push([from, toSpecial]);
      }
      else {
        // a general pattern
        if (prevC !== undefined && currC !== undefined && (prevC !== prevP || currC !== currP)) {
          const both = prevC + currC;
          // run thru all possible replacements to see if there's a match
          for (const p of this.possible) {
            if (both === p) {
              const from = prevP + currP;
              if (!results.some(r => r[0] === from)) { // only add if not already added
                const to = p[0] + ipaCombiners[0] + p[1];
                results.push([from, to]);
              }
            }
          }
        }
      }

      prevP = currP;
      prevC = click1Map[currP];
    }
  }
}
